import { api } from './index';

export interface Operator {
  id: string;
  name: string;
  email: string;
}

export interface CreateAccountData {
  name: string;
  email: string;
  phoneNumber?: string;
  username: string;
  password: string;
  role: string;
  isActive: boolean;
  permissionGroupId?: string | null;
  operatorId?: string; // For STAFF users - which operator they belong to
  buildingIds?: string[]; // Assigned buildings for STAFF
}

export interface UpdateAccountData {
  name?: string;
  email?: string;
  phoneNumber?: string;
  username?: string;
  password?: string;
  role?: string;
  isActive?: boolean;
  permissionGroupId?: string | null;
  operatorId?: string;
  buildingIds?: string[];
}

export interface Account {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  username: string;
  role: string;
  isActive: boolean;
  operatorId?: string;
  operator?: {
    id: string;
    name: string;
    email: string;
  };
  permissionGroup?: {
    id: string;
    name: string;
    description?: string;
  };
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  buildingIds?: string[];
  buildings?: Array<{
    id: string;
    name: string;
    projectCode?: string | null;
  }>;
}

export interface PermissionGroupOption {
  id: string;
  name: string;
  description?: string;
}

export const accountsApi = {
  // Get all accounts
  async getAll(
    page = 1,
    limit = 25,
    accountType?: 'staff' | 'resident',
    role?: string,
    search?: string,
  ): Promise<{
    data: Account[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (accountType) {
      params.append('accountType', accountType);
    } else if (role) {
      params.append('role', role);
    }

    if (search?.trim()) {
      params.append('search', search.trim());
    }

    const response = await api.get(`/users?${params.toString()}`);
    return response.data;
  },

  // Get single account
  async getById(id: string): Promise<Account> {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  // Create new account
  async create(data: CreateAccountData): Promise<Account> {
    const response = await api.post('/users', data);
    return response.data;
  },

  // Update account
  async update(id: string, data: UpdateAccountData): Promise<Account> {
    const response = await api.patch(`/users/${id}`, data);
    return response.data;
  },

  // Toggle account status
  async toggleStatus(id: string): Promise<Account> {
    const response = await api.patch(`/users/${id}/toggle-status`);
    return response.data;
  },

  // Delete account
  async delete(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  // Get available permission groups for account/task assignment
  async getPermissionGroups(role?: string, operatorId?: string): Promise<PermissionGroupOption[]> {
    const params = new URLSearchParams();
    if (role) params.append('role', role);
    if (operatorId) params.append('operatorId', operatorId);
    const response = await api.get(`/permission-groups?${params.toString()}`);
    return response.data.map((group: any) => ({
      id: group.id,
      name: group.name,
      description: group.description,
    }));
  },

  // Get operators for SUPERADMIN to choose from when creating STAFF users
  async getOperators(): Promise<Operator[]> {
    const response = await api.get('/users/operators');
    return response.data;
  },
}; 